import {useEffect,useMemo,useState} from 'react'
import {useNavigate} from 'react-router-dom'
import {Card,Badge,SafeIcon,Button,Table,Thead,Tbody,Tr,Th,Td} from '../components/UI'
import {Repository} from '../data/repository'
import {Invoice,EmailJob,EmailLog,RecurringProfile,PaymentRecord,Client} from '../types'
import {formatCurrency,formatDate,formatStatus} from '../utils/formatters'
import {startOfMonth,endOfMonth,subMonths} from 'date-fns'
import {calculatePaymentTotals} from '../utils/calculations'
import {FiClock,FiCheckCircle,FiAlertCircle,FiMail,FiRepeat,FiTrendingUp,FiFileText,FiArrowRight,FiBriefcase} from 'react-icons/fi'

type DashboardPeriod='current_month' | 'previous_month' | 'financial_year' | 'all_time'
type MonitorMode='recent' | 'attention'
type DeliveryTone='success' | 'warning' | 'danger' | 'neutral'

interface DeliveryInfo {
  label: string
  tone: DeliveryTone
  isFailure: boolean
}

const FAILURE_DELIVERY_STATES=new Set(['bounced','failed','rejected','suppressed','complained','blocked','fallback_failed','partial_failure'])
const SUCCESS_DELIVERY_STATES=new Set(['delivered','fallback_sent'])
const PENDING_DELIVERY_STATES=new Set(['pending','accepted','attempted','queued','processing','scheduled','temporary_failure','fallback_queued','fallback_processing'])

const normaliseStatus=(value?: string)=> String(value || '').trim().toLowerCase()
const titleCaseStatus=(value: string)=> value.replace(/_/g,' ').replace(/\b\w/g,char=> char.toUpperCase())

export const Dashboard=()=> {
  const [invoices,setInvoices]=useState<Invoice[]>([])
  const [payments,setPayments]=useState<PaymentRecord[]>([])
  const [emailJobs,setEmailJobs]=useState<EmailJob[]>([])
  const [emailLogs,setEmailLogs]=useState<EmailLog[]>([])
  const [recurringProfiles,setRecurringProfiles]=useState<RecurringProfile[]>([])
  const [clients,setClients]=useState<Client[]>([])
  const [jobs,setJobs]=useState<any[]>([])
  const [period,setPeriod]=useState<DashboardPeriod>('current_month')
  const [monitorMode,setMonitorMode]=useState<MonitorMode>('recent')
  const navigate=useNavigate()

  useEffect(()=> {
    loadData()
    const handleRefresh=()=> loadData()
    window.addEventListener('invoiceflow:refresh-data',handleRefresh)
    return ()=> window.removeEventListener('invoiceflow:refresh-data',handleRefresh)
  },[])

  const loadData=async ()=> {
    const [invs,pays,emails,logs,recs,cls,jbs]=await Promise.all([
      Repository.invoices.getAll(),
      Repository.payments.getAll(),
      Repository.emailJobs.getAll(),
      Repository.emailLogs.getAll(),
      Repository.recurringProfiles.getAll(),
      Repository.clients.getAll(),
      Repository.jobs.getAll()
    ])
    setInvoices(invs)
    setPayments(pays)
    setEmailJobs(emails)
    setEmailLogs(logs)
    setRecurringProfiles(recs)
    setClients(cls)
    setJobs(jbs)
  }

  const getPeriodBounds=(): {start: Date | null;end: Date | null;}=> {
    const now=new Date()
    if (period==='all_time') return {start: null,end: null}
    if (period==='previous_month') {
      const value=subMonths(now,1)
      return {start: startOfMonth(value),end: endOfMonth(value)}
    }
    if (period==='financial_year') {
      const startYear=now.getMonth() >=6 ? now.getFullYear() : now.getFullYear() - 1
      return {start: new Date(startYear,6,1),end: new Date(startYear + 1,5,30,23,59,59,999)}
    }
    return {start: startOfMonth(now),end: endOfMonth(now)}
  }

  const isInsideSelectedPeriod=(value?: string): boolean=> {
    if (!value) return false
    const date=new Date(value)
    if (Number.isNaN(date.getTime())) return false
    const {start,end}=getPeriodBounds()
    if (!start || !end) return true
    return date >=start && date <=end
  }

  const startOfToday=()=> {
    const value=new Date()
    value.setHours(0,0,0,0)
    return value
  }

  const daysPastDue=(dueDate?: string): number=> {
    if (!dueDate) return 0
    const due=new Date(dueDate)
    if (Number.isNaN(due.getTime())) return 0
    due.setHours(0,0,0,0)
    return Math.floor((startOfToday().getTime() - due.getTime()) / 86400000)
  }

  const isPastDueDate=(dueDate?: string)=> daysPastDue(dueDate) > 0
  const isDueWithinDays=(dueDate: string | undefined,days: number)=> {
    if (!dueDate) return false
    const due=new Date(dueDate)
    if (Number.isNaN(due.getTime())) return false
    due.setHours(0,0,0,0)
    const today=startOfToday()
    const end=new Date(today)
    end.setDate(end.getDate() + days)
    return due >=today && due <=end
  }

  const billableInvoices=invoices.filter(inv=> !['cancelled','void'].includes(inv.status))
  const periodInvoices=billableInvoices.filter(invoice=> isInsideSelectedPeriod(invoice.issueDate))
  const periodPayments=payments.filter(payment=> isInsideSelectedPeriod(payment.date))
  const paymentTotals=calculatePaymentTotals(periodPayments)
  const totalInvoiced=periodInvoices.reduce((sum,invoice)=> sum + Number(invoice.total || 0),0)
  const totalPaid=paymentTotals.totalPaid - paymentTotals.totalRefunded
  const totalUnpaid=periodInvoices.reduce((sum,invoice)=> sum + Math.max(0,Number(invoice.balanceDue || 0)),0)
  const overduePeriodInvoices=periodInvoices.filter(invoice=> Number(invoice.balanceDue || 0) > 0 && (invoice.status==='overdue' || isPastDueDate(invoice.dueDate)))
  const totalOverdue=overduePeriodInvoices.reduce((sum,invoice)=> sum + Math.max(0,Number(invoice.balanceDue || 0)),0)
  const collectionRate=totalInvoiced > 0 ? (totalPaid / totalInvoiced) * 100 : 0

  const invoiceIssueJobIds=useMemo(()=> new Set(
    emailJobs
      .filter(job=> ['invoice_sent','recurring_invoice'].includes(String(job.templateType || '')))
      .map(job=> job.id)
  ),[emailJobs])

  const latestInvoiceDeliveryLogs=useMemo(()=> {
    const map=new Map<string,EmailLog>()
    emailLogs.filter(log=> invoiceIssueJobIds.has(log.emailJobId)).forEach(log=> {
      const existing=map.get(log.invoiceId)
      const currentTime=new Date(log.providerEventAt || log.sentAt || log.failedAt || log.queuedAt || 0).getTime()
      const existingTime=existing ? new Date(existing.providerEventAt || existing.sentAt || existing.failedAt || existing.queuedAt || 0).getTime() : -1
      if (!existing || currentTime >=existingTime) map.set(log.invoiceId,log)
    })
    return map
  },[emailLogs,invoiceIssueJobIds])

  const getDeliveryInfo=(invoiceId: string): DeliveryInfo=> {
    const log=latestInvoiceDeliveryLogs.get(invoiceId)
    if (!log) return {label: 'Not sent',tone: 'neutral',isFailure: false}
    const finalStatus=normaliseStatus(log.finalDeliveryStatus)
    const fallbackStatus=normaliseStatus(log.fallbackStatus)
    const primaryStatus=normaliseStatus(log.primaryDeliveryStatus)
    const operationalStatus=normaliseStatus(log.status)
    const status=finalStatus || (fallbackStatus==='sent' ? 'fallback_sent' : '') || primaryStatus || operationalStatus

    if (status==='delivered') return {label: 'Delivered',tone: 'success',isFailure: false}
    if (status==='fallback_sent' || fallbackStatus==='sent') return {label: 'Fallback sent',tone: 'success',isFailure: false}
    if (log.suppressionType && !status) return {label: 'Suppressed',tone: 'danger',isFailure: true}
    if (FAILURE_DELIVERY_STATES.has(status)) return {label: titleCaseStatus(status),tone: 'danger',isFailure: true}
    if (PENDING_DELIVERY_STATES.has(status)) return {label: status==='temporary_failure' ? 'Delivery delayed' : 'Pending',tone: 'warning',isFailure: false}
    if (status==='sent') return {label: 'Awaiting delivery',tone: 'warning',isFailure: false}
    if (SUCCESS_DELIVERY_STATES.has(status)) return {label: titleCaseStatus(status),tone: 'success',isFailure: false}
    return {label: status ? titleCaseStatus(status) : 'Awaiting delivery',tone: 'warning',isFailure: false}
  }

  const portfolioOutstandingInvoices=billableInvoices.filter(inv=> Number(inv.balanceDue || 0) > 0)
  const portfolioOutstanding=portfolioOutstandingInvoices.reduce((sum,inv)=> sum + Math.max(0,Number(inv.balanceDue || 0)),0)
  const overduePortfolioInvoices=portfolioOutstandingInvoices.filter(inv=> inv.status==='overdue' || isPastDueDate(inv.dueDate))
  const overduePortfolioAmount=overduePortfolioInvoices.reduce((sum,inv)=> sum + Math.max(0,Number(inv.balanceDue || 0)),0)
  const dueNext7Invoices=portfolioOutstandingInvoices.filter(inv=> isDueWithinDays(inv.dueDate,7))
  const dueNext7Amount=dueNext7Invoices.reduce((sum,inv)=> sum + Math.max(0,Number(inv.balanceDue || 0)),0)
  const draftInvoices=billableInvoices.filter(inv=> ['draft','approved'].includes(inv.status))
  const deliveryIssueInvoices=billableInvoices.filter(inv=> getDeliveryInfo(inv.id).isFailure)
  const queuedEmails=emailJobs.filter(j=> j.status==='queued').length

  const recurringDueProfiles=recurringProfiles.filter(profile=> {
    if (profile.status !=='active' || !profile.nextInvoiceDate) return false
    const nextDate=new Date(profile.nextInvoiceDate)
    if (Number.isNaN(nextDate.getTime())) return false
    nextDate.setHours(0,0,0,0)
    const end=new Date(startOfToday())
    end.setDate(end.getDate() + 7)
    return nextDate <=end
  })

  const ageing=portfolioOutstandingInvoices.reduce((acc,inv)=> {
    const amount=Math.max(0,Number(inv.balanceDue || 0))
    const days=daysPastDue(inv.dueDate)
    if (days <=0) acc.notDue +=amount
    else if (days <=30) acc.days1to30 +=amount
    else if (days <=60) acc.days31to60 +=amount
    else acc.days61plus +=amount
    return acc
  },{notDue: 0,days1to30: 0,days31to60: 0,days61plus: 0})

  const clientOutstanding=useMemo(()=> {
    const totals=new Map<string,number>()
    portfolioOutstandingInvoices.forEach(inv=> totals.set(inv.clientId,(totals.get(inv.clientId) || 0) + Math.max(0,Number(inv.balanceDue || 0))))
    return [...totals.entries()].sort((a,b)=> b[1] - a[1])[0] || null
  },[portfolioOutstandingInvoices])
  const largestOutstandingClient=clientOutstanding ? clients.find(client=> client.id===clientOutstanding[0]) : undefined
  const largestOutstandingAmount=clientOutstanding?.[1] || 0

  const invoiceNeedsAttention=(inv: Invoice)=> {
    if (getDeliveryInfo(inv.id).isFailure) return true
    if (['draft','approved'].includes(inv.status)) return true
    if (Number(inv.balanceDue || 0) <=0) return false
    return isPastDueDate(inv.dueDate) || isDueWithinDays(inv.dueDate,7)
  }

  const monitorInvoices=[...invoices]
    .filter(inv=> monitorMode==='recent' ? true : invoiceNeedsAttention(inv))
    .sort((a,b)=> {
      if (monitorMode==='attention') {
        const severity=(inv: Invoice)=> getDeliveryInfo(inv.id).isFailure || isPastDueDate(inv.dueDate) ? 0 : isDueWithinDays(inv.dueDate,7) ? 1 : 2
        const severityDiff=severity(a) - severity(b)
        if (severityDiff !==0) return severityDiff
      }
      return new Date(b.issueDate).getTime() - new Date(a.issueDate).getTime()
    })
    .slice(0,6)

  const activeJobs=jobs.filter(job=> String(job.status || '').toLowerCase()==='active')
  const mobileRecentInvoices=[...invoices].sort((a,b)=> new Date(b.issueDate || 0).getTime() - new Date(a.issueDate || 0).getTime()).slice(0,4)

  const getDueText=(inv: Invoice)=> {
    if (!inv.dueDate) return 'No due date'
    const days=daysPastDue(inv.dueDate)
    if (Number(inv.balanceDue || 0) <=0) return formatDate(inv.dueDate)
    if (days >0) return `${formatDate(inv.dueDate)} · ${days}d overdue`
    if (isDueWithinDays(inv.dueDate,7)) {
      const due=new Date(inv.dueDate);due.setHours(0,0,0,0)
      const diff=Math.max(0,Math.round((due.getTime() - startOfToday().getTime()) / 86400000))
      return `${formatDate(inv.dueDate)} · due in ${diff}d`
    }
    return formatDate(inv.dueDate)
  }

  const StatCard=({title,value,detail,icon: Icon,colorClass,destination}: any)=> (
    <button type="button" onClick={()=> navigate(destination)} className="w-full rounded-xl text-left outline-none focus-visible:ring-2 focus-visible:ring-brand-500/30" aria-label={`Open ${title}`}>
      <Card className="p-4 flex h-full items-center gap-4 cursor-pointer transition-colors hover:border-brand-300 hover:bg-brand-50/20">
        <div className={`p-3 rounded-lg ${colorClass}`}><SafeIcon icon={Icon} className="w-5 h-5" /></div>
        <div className="min-w-0">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">{title}</p>
          <h4 className="text-xl font-bold text-gray-900 mt-0.5">{value}</h4>
          {detail && <p className="mt-1 text-xs font-medium text-gray-500">{detail}</p>}
        </div>
      </Card>
    </button>
  )

  const AttentionCard=({title,value,detail,icon: Icon,tone='neutral',destination}: any)=> {
    const styles=tone==='danger'
      ? 'border-rose-200 bg-rose-50/70 text-rose-700'
      : tone==='warning'
        ? 'border-amber-200 bg-amber-50/70 text-amber-700'
        : 'border-gray-200 bg-white text-gray-600'
    return (
      <button type="button" onClick={()=> navigate(destination)} className={`rounded-xl border p-4 text-left transition-colors hover:border-brand-300 ${styles}`}>
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-start gap-3 min-w-0">
            <div className="rounded-lg bg-white/80 p-2 shadow-sm"><SafeIcon icon={Icon} className="h-4 w-4" /></div>
            <div className="min-w-0">
              <p className="text-xs font-semibold uppercase tracking-wider opacity-80">{title}</p>
              <p className="mt-1 text-lg font-black text-gray-900">{value}</p>
              <p className="mt-0.5 text-xs opacity-80">{detail}</p>
            </div>
          </div>
          <FiArrowRight className="mt-1 shrink-0 opacity-40" />
        </div>
      </button>
    )
  }

  const DeliveryBadge=({info}: {info: DeliveryInfo})=> (
    <Badge variant={info.tone==='success' ? 'success' : info.tone==='danger' ? 'danger' : info.tone==='warning' ? 'warning' : 'neutral'}>{info.label}</Badge>
  )

  const MobileMetricCard=({title,value,detail,icon: Icon,tone,destination}: any)=> {
    const toneMap: Record<string,string>={
      sky: 'bg-sky-50 text-sky-700',
      emerald: 'bg-emerald-50 text-emerald-700',
      amber: 'bg-amber-50 text-amber-700',
      rose: 'bg-rose-50 text-rose-700'
    }
    return (
      <button type="button" onClick={()=> navigate(destination)} className="min-h-[116px] rounded-2xl border border-slate-200 bg-white p-3 text-left shadow-sm transition active:scale-[0.99] active:bg-slate-50" aria-label={`Open ${title}`}>
        <div className="flex h-full items-start gap-3">
          <span className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl ${toneMap[tone] || toneMap.sky}`}><SafeIcon icon={Icon} className="h-5 w-5" /></span>
          <span className="min-w-0 flex-1">
            <span className="block text-[11px] font-bold uppercase tracking-[0.12em] text-slate-500">{title}</span>
            <span className="mt-1 block truncate text-[18px] font-black leading-tight text-slate-950">{value}</span>
            <span className="mt-1 block text-[11px] leading-snug text-slate-500">{detail}</span>
          </span>
        </div>
      </button>
    )
  }

  const ageingRows=[
    ['Not yet due',ageing.notDue,'text-emerald-700'],
    ['1–30 days overdue',ageing.days1to30,'text-amber-700'],
    ['31–60 days overdue',ageing.days31to60,'text-orange-700'],
    ['61+ days overdue',ageing.days61plus,'text-rose-700']
  ] as const

  return (
    <div className="w-full min-w-0">
      <div data-dashboard-mobile="true" className="sm:hidden space-y-4 pb-2">
        <div className="flex items-end justify-between gap-3">
          <div className="min-w-0">
            <h2 className="text-[28px] font-black leading-none tracking-tight text-slate-950">Dashboard</h2>
            <p className="mt-1.5 text-[13px] leading-snug text-slate-500">Your invoicing snapshot at a glance.</p>
          </div>
          <select value={period} onChange={(e: any)=> setPeriod(e.target.value as DashboardPeriod)} className="min-h-11 max-w-[150px] rounded-xl border border-slate-200 bg-white px-3 text-base font-semibold text-slate-700 shadow-sm outline-none">
            <option value="current_month">Current Month</option>
            <option value="previous_month">Previous Month</option>
            <option value="financial_year">Financial Year</option>
            <option value="all_time">All Time</option>
          </select>
        </div>

        <div className="grid grid-cols-2 gap-2.5">
          <MobileMetricCard title="Invoiced" value={formatCurrency(totalInvoiced)} detail={`${periodInvoices.length} invoice${periodInvoices.length===1 ? '' : 's'}`} icon={FiTrendingUp} tone="sky" destination="/invoices" />
          <MobileMetricCard title="Collected" value={formatCurrency(totalPaid)} detail={`${collectionRate.toFixed(1)}% of invoiced`} icon={FiCheckCircle} tone="emerald" destination="/payments-recurring" />
          <MobileMetricCard title="Outstanding" value={formatCurrency(totalUnpaid)} detail={`${periodInvoices.filter(inv=> Number(inv.balanceDue || 0)>0).length} open invoice${periodInvoices.filter(inv=> Number(inv.balanceDue || 0)>0).length===1 ? '' : 's'}`} icon={FiClock} tone="amber" destination="/invoices" />
          <MobileMetricCard title="Overdue" value={formatCurrency(totalOverdue)} detail={overduePeriodInvoices.length ? `${overduePeriodInvoices.length} overdue` : 'All clear'} icon={FiAlertCircle} tone="rose" destination="/invoices" />
        </div>

        <button type="button" onClick={()=> navigate('/payments-recurring')} className="w-full rounded-2xl border border-slate-200 bg-white p-4 text-left shadow-sm">
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="text-[15px] font-black text-slate-950">Receivables Health</p>
              <p className="mt-0.5 text-[12px] text-slate-500">{collectionRate.toFixed(1)}% collected for this period</p>
            </div>
            <p className="text-[13px] font-bold text-slate-600">{formatCurrency(totalPaid)} <span className="font-medium text-slate-400">/ {formatCurrency(totalInvoiced)}</span></p>
          </div>
          <div className="mt-4 h-3 overflow-hidden rounded-full bg-slate-100">
            <div className="h-full rounded-full bg-brand-600 transition-all" style={{width: `${Math.min(100,Math.max(0,collectionRate))}%`}} />
          </div>
          <div className="mt-3 flex items-center justify-between gap-3 text-[11px]">
            <span className="font-semibold text-emerald-700">Collected {formatCurrency(totalPaid)}</span>
            <span className="font-semibold text-amber-700">Outstanding {formatCurrency(totalUnpaid)}</span>
          </div>
        </button>

        <section className="overflow-hidden rounded-2xl border border-rose-100 bg-white shadow-sm">
          <button type="button" onClick={()=> navigate('/invoices')} className="flex w-full items-center justify-between gap-3 bg-rose-50/70 px-4 py-3 text-left">
            <div><p className="text-[15px] font-black text-slate-950">Focus Today</p><p className="text-[11px] text-slate-500">Items that may need your attention</p></div>
            <span className="text-xs font-bold text-slate-500">{portfolioOutstandingInvoices.length + deliveryIssueInvoices.length + activeJobs.length} items</span>
          </button>
          <div className="divide-y divide-slate-100">
            <button type="button" onClick={()=> navigate('/invoices')} className="flex w-full items-center gap-3 px-4 py-3 text-left">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-rose-50 text-rose-600"><SafeIcon icon={FiAlertCircle} className="h-5 w-5" /></span>
              <span className="min-w-0 flex-1"><span className="block text-sm font-bold text-slate-900">Open Invoices</span><span className="block text-[11px] text-slate-500">Invoices awaiting payment</span></span>
              <span className="rounded-full bg-rose-50 px-2.5 py-1 text-xs font-black text-rose-700">{portfolioOutstandingInvoices.length}</span>
              <FiArrowRight className="shrink-0 text-slate-300" />
            </button>
            <button type="button" onClick={()=> navigate('/jobs-documents')} className="flex w-full items-center gap-3 px-4 py-3 text-left">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-indigo-50 text-indigo-600"><SafeIcon icon={FiBriefcase} className="h-5 w-5" /></span>
              <span className="min-w-0 flex-1"><span className="block text-sm font-bold text-slate-900">Active Jobs</span><span className="block text-[11px] text-slate-500">Jobs currently in progress</span></span>
              <span className="rounded-full bg-indigo-50 px-2.5 py-1 text-xs font-black text-indigo-700">{activeJobs.length}</span>
              <FiArrowRight className="shrink-0 text-slate-300" />
            </button>
            <button type="button" onClick={()=> navigate('/diagnostics')} className="flex w-full items-center gap-3 px-4 py-3 text-left">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-slate-100 text-slate-600"><SafeIcon icon={FiMail} className="h-5 w-5" /></span>
              <span className="min-w-0 flex-1"><span className="block text-sm font-bold text-slate-900">Emails to Review</span><span className="block text-[11px] text-slate-500">{deliveryIssueInvoices.length ? 'Delivery issues need attention' : queuedEmails ? 'Emails waiting in queue' : 'No current delivery issues'}</span></span>
              <span className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-black text-slate-700">{deliveryIssueInvoices.length + queuedEmails}</span>
              <FiArrowRight className="shrink-0 text-slate-300" />
            </button>
          </div>
        </section>

        <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
          <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3">
            <div><p className="text-[15px] font-black text-slate-950">Recent Invoices</p><p className="text-[11px] text-slate-500">Latest invoice activity</p></div>
            <button type="button" onClick={()=> navigate('/invoices')} className="min-h-10 rounded-xl px-2 text-xs font-bold text-brand-700">View All</button>
          </div>
          <div className="divide-y divide-slate-100">
            {mobileRecentInvoices.length===0 ? (
              <div className="px-4 py-8 text-center text-sm text-slate-400">No invoices yet.</div>
            ) : mobileRecentInvoices.map(inv=> {
              const client=clients.find(c=> c.id===inv.clientId)
              return (
                <button key={inv.id} type="button" onClick={()=> navigate('/invoices')} className="flex w-full items-center gap-3 px-4 py-3 text-left active:bg-slate-50">
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-slate-50 text-slate-500"><SafeIcon icon={FiFileText} className="h-4 w-4" /></span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-black text-slate-900">{inv.invoiceNumber}</span>
                    <span className="block truncate text-[11px] text-slate-500">{client?.name || 'Unknown client'}</span>
                  </span>
                  <span className="shrink-0 text-right">
                    <span className="block text-sm font-black text-slate-900">{formatCurrency(inv.total,inv.currency)}</span>
                    <span className="mt-0.5 block text-[10px] font-semibold text-slate-400">{formatStatus(inv.status)}</span>
                  </span>
                  <FiArrowRight className="shrink-0 text-slate-300" />
                </button>
              )
            })}
          </div>
        </section>
      </div>

      <div data-dashboard-standard="true" className="hidden sm:block space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:justify-between sm:items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Dashboard</h2>
          <p className="text-sm text-gray-500 mt-1">Invoice performance, receivables health and items that need attention.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <select value={period} onChange={(e: any)=> setPeriod(e.target.value as DashboardPeriod)} className="border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white text-gray-700 outline-none focus:border-brand-500">
            <option value="current_month">Current Month</option>
            <option value="previous_month">Previous Month</option>
            <option value="financial_year">Current Financial Year</option>
            <option value="all_time">All Time</option>
          </select>
          <Badge variant="brand">{periodInvoices.length} Invoices in View</Badge>
          <Badge variant="neutral">{invoices.length} Total Invoices</Badge>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
        <StatCard title="Invoiced (Period)" value={formatCurrency(totalInvoiced)} detail={`${periodInvoices.length} invoice${periodInvoices.length===1 ? '' : 's'}`} icon={FiTrendingUp} colorClass="bg-sky-50 text-sky-600" destination="/invoices" />
        <StatCard title="Collected (Period)" value={formatCurrency(totalPaid)} detail={totalInvoiced > 0 ? `${collectionRate.toFixed(1)}% of period invoiced` : 'No invoices in period'} icon={FiCheckCircle} colorClass="bg-emerald-50 text-emerald-600" destination="/payments-recurring" />
        <StatCard title="Outstanding (Period)" value={formatCurrency(totalUnpaid)} detail={`${periodInvoices.filter(inv=> Number(inv.balanceDue || 0) > 0).length} open invoice${periodInvoices.filter(inv=> Number(inv.balanceDue || 0) > 0).length===1 ? '' : 's'}`} icon={FiClock} colorClass="bg-amber-50 text-amber-600" destination="/invoices" />
        <StatCard title="Overdue (Period)" value={formatCurrency(totalOverdue)} detail={overduePeriodInvoices.length ? `${overduePeriodInvoices.length} invoice${overduePeriodInvoices.length===1 ? '' : 's'} overdue` : 'All clear'} icon={FiAlertCircle} colorClass="bg-rose-50 text-rose-600" destination="/invoices" />
      </div>

      <section className="space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h3 className="text-xs font-bold text-gray-400 uppercase tracking-[0.2em]">Needs Attention</h3>
          <span className="text-xs text-gray-400">Across the full portfolio</span>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-5 gap-3">
          <AttentionCard title="Overdue" value={overduePortfolioInvoices.length ? `${overduePortfolioInvoices.length} · ${formatCurrency(overduePortfolioAmount)}` : '0'} detail={overduePortfolioInvoices.length ? 'Open overdue invoices' : 'All clear'} icon={FiAlertCircle} tone={overduePortfolioInvoices.length ? 'danger' : 'neutral'} destination="/invoices" />
          <AttentionCard title="Email Delivery" value={deliveryIssueInvoices.length.toString()} detail={deliveryIssueInvoices.length ? 'Delivery issue to review' : 'No current failures'} icon={FiMail} tone={deliveryIssueInvoices.length ? 'danger' : 'neutral'} destination="/diagnostics" />
          <AttentionCard title="Email Queue" value={queuedEmails.toString()} detail={queuedEmails ? 'Waiting to send' : 'All caught up'} icon={FiMail} tone={queuedEmails ? 'warning' : 'neutral'} destination="/diagnostics" />
          <AttentionCard title="Recurring Due" value={recurringDueProfiles.length.toString()} detail={recurringDueProfiles.length ? 'Due within 7 days' : 'All up to date'} icon={FiRepeat} tone={recurringDueProfiles.length ? 'warning' : 'neutral'} destination="/payments-recurring" />
          <AttentionCard title="Draft / Unsent" value={draftInvoices.length.toString()} detail={draftInvoices.length ? 'Review before issue' : 'All clear'} icon={FiFileText} tone={draftInvoices.length ? 'warning' : 'neutral'} destination="/invoices" />
        </div>
      </section>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        <Card className="xl:col-span-2 p-0 overflow-hidden">
          <div className="p-4 border-b border-gray-100 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h4 className="font-bold text-gray-900">Invoice Monitor</h4>
              <p className="text-xs text-gray-500 mt-0.5">Latest invoices with balance, due date and delivery state.</p>
            </div>
            <div className="inline-flex rounded-lg bg-gray-100 p-1 self-start sm:self-auto">
              <button type="button" onClick={()=> setMonitorMode('recent')} className={`px-3 py-1.5 rounded-md text-xs font-bold ${monitorMode==='recent' ? 'bg-white text-brand-700 shadow-sm' : 'text-gray-500'}`}>Recent</button>
              <button type="button" onClick={()=> setMonitorMode('attention')} className={`px-3 py-1.5 rounded-md text-xs font-bold ${monitorMode==='attention' ? 'bg-white text-brand-700 shadow-sm' : 'text-gray-500'}`}>Needs Attention</button>
            </div>
          </div>

          <div className="xl:hidden divide-y divide-gray-100">
            {monitorInvoices.length===0 ? <div className="p-8 text-center text-sm text-gray-400">Nothing needs attention right now.</div> : monitorInvoices.map(inv=> {
              const client=clients.find(c=> c.id===inv.clientId)
              const delivery=getDeliveryInfo(inv.id)
              return (
                <button key={inv.id} type="button" onClick={()=> navigate('/invoices')} className="w-full p-4 text-left hover:bg-gray-50">
                  <div className="flex items-start justify-between gap-3">
                    <div><p className="font-bold text-gray-900">{inv.invoiceNumber}</p><p className="text-xs text-gray-500">{client?.name || 'Unknown client'}</p></div>
                    <Badge variant={inv.status==='paid' ? 'success' : inv.status==='overdue' ? 'danger' : 'warning'}>{formatStatus(inv.status)}</Badge>
                  </div>
                  <div className="grid grid-cols-2 gap-2 mt-3 text-xs">
                    <div><span className="text-gray-400">Total</span><p className="font-bold text-gray-900">{formatCurrency(inv.total,inv.currency)}</p></div>
                    <div><span className="text-gray-400">Balance</span><p className={Number(inv.balanceDue || 0) > 0 ? 'font-bold text-amber-700' : 'font-bold text-emerald-700'}>{formatCurrency(inv.balanceDue,inv.currency)}</p></div>
                    <div className="col-span-2"><span className="text-gray-400">Due</span><p className={isPastDueDate(inv.dueDate) && Number(inv.balanceDue || 0) > 0 ? 'font-medium text-rose-700' : 'font-medium text-gray-700'}>{getDueText(inv)}</p></div>
                  </div>
                  <div className="mt-3"><DeliveryBadge info={delivery} /></div>
                </button>
              )
            })}
          </div>

          <div className="hidden xl:block overflow-x-auto">
            <Table className="min-w-[900px]">
              <Thead><Tr><Th>Invoice #</Th><Th>Client</Th><Th className="text-right">Total</Th><Th className="text-right">Balance</Th><Th>Due Date</Th><Th>Status</Th><Th>Email Delivery</Th></Tr></Thead>
              <Tbody>
                {monitorInvoices.length===0 ? <Tr><Td colSpan={7} className="text-center py-10 text-gray-400">Nothing needs attention right now.</Td></Tr> : monitorInvoices.map(inv=> {
                  const client=clients.find(c=> c.id===inv.clientId)
                  const delivery=getDeliveryInfo(inv.id)
                  return (
                    <Tr key={inv.id} className="cursor-pointer hover:bg-gray-50" onClick={()=> navigate('/invoices')}>
                      <Td className="font-bold text-gray-900 whitespace-nowrap">{inv.invoiceNumber}</Td>
                      <Td>{client?.name || 'Unknown client'}</Td>
                      <Td className="text-right whitespace-nowrap">{formatCurrency(inv.total,inv.currency)}</Td>
                      <Td className={`text-right font-bold whitespace-nowrap ${Number(inv.balanceDue || 0) > 0 ? 'text-amber-700' : 'text-emerald-700'}`}>{formatCurrency(inv.balanceDue,inv.currency)}</Td>
                      <Td sortValue={inv.dueDate} className={`whitespace-nowrap ${isPastDueDate(inv.dueDate) && Number(inv.balanceDue || 0) > 0 ? 'text-rose-700 font-semibold' : ''}`}>{getDueText(inv)}</Td>
                      <Td><Badge variant={inv.status==='paid' ? 'success' : inv.status==='overdue' ? 'danger' : inv.status==='cancelled' ? 'neutral' : 'warning'}>{formatStatus(inv.status)}</Badge></Td>
                      <Td sortValue={delivery.label}><DeliveryBadge info={delivery} /></Td>
                    </Tr>
                  )
                })}
              </Tbody>
            </Table>
          </div>
          <div className="p-3 border-t border-gray-100 flex justify-between items-center text-xs text-gray-500">
            <span>{monitorMode==='recent' ? `Showing latest ${monitorInvoices.length}` : `${monitorInvoices.length} item${monitorInvoices.length===1 ? '' : 's'} needing attention`}</span>
            <Button variant="ghost" size="sm" onClick={()=> navigate('/invoices')}>View all invoices <FiArrowRight /></Button>
          </div>
        </Card>

        <div className="space-y-4">
          <Card className="p-0 overflow-hidden">
            <div className="p-4 border-b border-gray-100"><h4 className="font-bold text-gray-900">Receivables Ageing</h4><p className="text-xs text-gray-500 mt-0.5">Current outstanding portfolio</p></div>
            <div className="p-4 space-y-3">
              {ageingRows.map(([label,value,color])=> {
                const pct=portfolioOutstanding > 0 ? (value / portfolioOutstanding) * 100 : 0
                return <div key={label} className="flex items-center justify-between gap-3"><div><p className={`text-sm font-medium ${color}`}>{label}</p><p className="text-xs text-gray-400">{pct.toFixed(1)}% of outstanding</p></div><p className="font-bold text-gray-900 whitespace-nowrap">{formatCurrency(value)}</p></div>
              })}
              <div className="pt-3 mt-3 border-t border-gray-100 flex items-center justify-between"><span className="text-sm font-bold text-gray-700">Total outstanding</span><span className="text-lg font-black text-gray-900">{formatCurrency(portfolioOutstanding)}</span></div>
            </div>
          </Card>

          <Card className="p-4">
            <p className="text-xs font-bold text-gray-400 uppercase tracking-wider">Cash Collection Focus</p>
            <div className="mt-3 grid grid-cols-2 gap-3">
              <div className="rounded-lg bg-amber-50 p-3"><p className="text-[11px] font-bold text-amber-700 uppercase">Due next 7 days</p><p className="mt-1 text-lg font-black text-gray-900">{formatCurrency(dueNext7Amount)}</p><p className="text-xs text-gray-500">{dueNext7Invoices.length} invoice{dueNext7Invoices.length===1 ? '' : 's'}</p></div>
              <div className="rounded-lg bg-rose-50 p-3"><p className="text-[11px] font-bold text-rose-700 uppercase">Overdue</p><p className="mt-1 text-lg font-black text-gray-900">{formatCurrency(overduePortfolioAmount)}</p><p className="text-xs text-gray-500">{overduePortfolioInvoices.length} invoice{overduePortfolioInvoices.length===1 ? '' : 's'}</p></div>
            </div>
            <div className="mt-4 pt-4 border-t border-gray-100">
              <p className="text-xs text-gray-400">Largest outstanding client</p>
              <div className="flex items-end justify-between gap-3 mt-1"><div><p className="font-bold text-gray-900">{largestOutstandingClient?.name || 'None'}</p><p className="text-xs text-gray-500">{largestOutstandingClient ? `${portfolioOutstandingInvoices.filter(inv=> inv.clientId===largestOutstandingClient.id).length} open invoice${portfolioOutstandingInvoices.filter(inv=> inv.clientId===largestOutstandingClient.id).length===1 ? '' : 's'}` : 'No outstanding balance'}</p></div><p className="font-black text-gray-900">{formatCurrency(largestOutstandingAmount)}</p></div>
            </div>
          </Card>

          <Card className="p-4">
            <div className="flex items-center justify-between"><div><p className="text-xs font-bold text-gray-400 uppercase tracking-wider">Collection Rate (Period)</p><p className="text-2xl font-black text-brand-700 mt-1">{collectionRate.toFixed(1)}%</p></div><p className="text-xs text-gray-500 text-right">{formatCurrency(totalPaid)} collected<br/>of {formatCurrency(totalInvoiced)} invoiced</p></div>
            <div className="mt-3 h-2 rounded-full bg-gray-100 overflow-hidden"><div className="h-full bg-brand-600 rounded-full" style={{width: `${Math.min(100,Math.max(0,collectionRate))}%`}} /></div>
          </Card>
        </div>
      </div>
      </div>
    </div>
  )
}